import { createActions, createReducer } from "reduxsauce"
import { first, get, omit, size } from "lodash"
import { takeLatest, call, select, put } from "redux-saga/effects"
import { getOptimizeDate } from "modules/optimization/selectors"
import { getViewType, getSelectedModelId } from "modules/portfolio-root/selectors/portfolios"
import { Types as BaseTypes } from "modules/portfolio-root/model"
import { post as apiPost } from "api"
import { Types as LoaderTypes } from "model/data"
import { LOADING, ERROR, OK } from "helpers/data-statuses"
import { getBasePositionSet } from "selectors/base"
import { getForecast } from "modules/optimization/selectors/data"
import { Types as OptimizationTypes } from "modules/optimization/model"
import { getApiConstraints } from "modules/optimization/selectors/constraints"
import { Creators as notificationCreators } from "modules/notifications/model"
import { VIEW_TYPES } from "helpers/base"

export const { Types, Creators } = createActions({
  optimizationScanner: [],
  optimizationScannerSuccess: ["optimizationResult"],
  optimizationScannerError: [],
}, {
  prefix: "ompnt/modules/optimizationScanner/",
})

const DEFAULT_STATE = {
  optimizationScannerData: [],
  status: LOADING,
}

function handleOptimizationScannerSuccess(state, { optimizationResult }) {
  return {
    ...state,
    optimizationScannerData: optimizationResult,
    status: OK,
  }
}

function handleOptimizationScannerError(state) {
  return { ...state, error: "A technical error has happened. Please try again later.", status: ERROR }
}

function handleLoadSuccess(state, action) {
  if (action.resource === "portfolio") {
    return DEFAULT_STATE
  } else {
    return state
  }
}

function handleReload() {
  return DEFAULT_STATE
}

export default createReducer(DEFAULT_STATE, {
  [Types.OPTIMIZATION_SCANNER_SUCCESS]: handleOptimizationScannerSuccess,
  [Types.OPTIMIZATION_SCANNER_ERROR]: handleOptimizationScannerError,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [OptimizationTypes.SET_CONSTRAINTS]: handleReload,
  [OptimizationTypes.REMOVE_FACTOR]: handleReload,
  [OptimizationTypes.RESET_SETTINGS]: handleReload,
  [OptimizationTypes.SET_SETTINGS]: handleReload,
  [OptimizationTypes.LOAD_SECURITIES_CONSTRAINTS]: handleReload,
  [OptimizationTypes.RELOAD_CONSTRAINTS]: handleReload,
  [BaseTypes.SET_DATE]: handleReload,
  [BaseTypes.SET_SELECTED_BASE_SUCCESS]: handleReload,
  [BaseTypes.SET_MODEL]: handleReload,
})

function* callOptimization() {
  const { account, portfolioShortId, selectedDate, modelId, forecast: rawForecast, constraints, viewType, base } = yield select(state => ({
    account: state.global.selected.account,
    portfolioShortId: state.global.selected.portfolio,
    selectedDate: getOptimizeDate(state),
    modelId: getSelectedModelId(state),
    forecast: getForecast(state),
    constraints: getApiConstraints(state),
    base: getBasePositionSet(state),
    viewType: getViewType(state),
  }))

  // Omit the horizon so the optimizer think the provided forecast is annualized even if the selected horizon is != 252 to match the scorecard values
  const forecast = omit(rawForecast, "horizon")
  try {
    const { data, errors, messages } = yield call(apiPost, `/api/${account}/p/${portfolioShortId}/m/${modelId}/optimization-scanner-chart/${selectedDate}`, {
      objective: [{}],
      constraints,
      base: viewType === VIEW_TYPES.ACTIVE ? base : null,
      forecast,
    })

    if (size(errors)) {
      yield put(notificationCreators.addApiNotification("error", null, get(first(errors), "code"), get(first(errors), "message")))
      yield put(Creators.optimizationScannerError())
    } else if (size(messages)) {
      yield put(notificationCreators.addApiNotification("warning", null, get(first(messages), "code"), get(first(messages), "message")))

      if (data) {
        yield put(Creators.optimizationScannerSuccess(data))
      } else {
        yield put(Creators.optimizationScannerError())
      }
    } else {
      yield put(Creators.optimizationScannerSuccess(data))
    }
  } catch {
    yield put(Creators.optimizationScannerError())
  }
}

export function* saga() {
  // Don't add OptimizationTypes.SET_SETTINGS because it will cause a double optimization
  // SET_SETTINGS is used for changing the forecast. Changing the forecast forces the whole <Optimization /> module to re-render
  // which will trigger a new optimization.
  yield takeLatest([
    OptimizationTypes.SET_CONSTRAINTS,
    Types.OPTIMIZATION_SCANNER,
    OptimizationTypes.REMOVE_FACTOR,
    OptimizationTypes.RESET_SETTINGS,
  ], callOptimization)
}
